import { ArrowUpRight, Star } from "lucide-react";
import { Link } from "react-router-dom";
import { collegeCategoryMeta } from "../../data/colleges";
import { getCollegeLogoCandidate } from "../../utils/collegeHelpers";
import LogoPlaceholder from "./LogoPlaceholder";

function CollegeCard({
  college,
  compact = false,
  minimal = false,
  showCta = true,
  ctaLabel = "Get Guidance",
  ctaTo = "/contact",
  className = ""
}) {
  const logoCandidate = getCollegeLogoCandidate(college.logoKey);
  const visibleTags = compact ? college.categoryTags.slice(0, 2) : college.categoryTags;

  if (minimal) {
    return (
      <article
        className={`h-full rounded-2xl border border-brand-line bg-white p-4 shadow-card transition duration-300 hover:-translate-y-0.5 hover:shadow-card-hover ${className}`.trim()}
      >
        <div className="flex items-center gap-3">
          {logoCandidate ? (
            <img
              src={logoCandidate}
              alt={`${college.name} logo`}
              className="h-12 w-12 rounded-xl border border-brand-line bg-white object-contain"
            />
          ) : (
            <LogoPlaceholder name={college.name} />
          )}

          <h3 className="text-base font-semibold leading-tight text-brand-navy sm:text-lg">{college.name}</h3>
        </div>
      </article>
    );
  }

  return (
    <article
      className={`relative h-full rounded-2xl border border-brand-line bg-white p-5 shadow-card transition duration-300 hover:-translate-y-0.5 hover:shadow-card-hover ${
        college.featured ? "ring-1 ring-brand-gold/45" : ""
      } ${className}`.trim()}
    >
      {college.featured ? (
        <span className="absolute right-4 top-4 inline-flex items-center gap-1 rounded-full bg-brand-gold-soft px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.1em] text-brand-charcoal">
          <Star className="h-3 w-3 text-brand-gold-strong" />
          Featured
        </span>
      ) : null}

      <div className="flex items-start gap-3">
        {logoCandidate ? (
          <img
            src={logoCandidate}
            alt={`${college.name} logo`}
            className="h-12 w-12 rounded-xl border border-brand-line bg-white object-contain"
          />
        ) : (
          <LogoPlaceholder name={college.name} />
        )}

        <div className="min-w-0 flex-1">
          <h3 className="text-lg font-semibold leading-tight text-brand-navy">{college.name}</h3>
          <p className="mt-1 text-xs font-medium uppercase tracking-[0.1em] text-brand-charcoal/64">{college.location}</p>
        </div>
      </div>

      {!compact ? (
        <p className="mt-4 text-sm leading-relaxed text-brand-charcoal/84">{college.description}</p>
      ) : null}

      <div className="mt-4 flex flex-wrap gap-2">
        {visibleTags.map((tag) => {
          const tagMeta = collegeCategoryMeta[tag] || {
            label: tag,
            tone: "bg-brand-sky/55 text-brand-navy"
          };

          return (
            <span
              key={`${college.id}-${tag}`}
              className={`rounded-full px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.1em] ${tagMeta.tone}`}
            >
              {tagMeta.label}
            </span>
          );
        })}
      </div>

      {showCta ? (
        <Link
          to={ctaTo}
          className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-brand-navy hover:text-brand-gold-strong"
        >
          {ctaLabel}
          <ArrowUpRight className="h-4 w-4" />
        </Link>
      ) : null}
    </article>
  );
}

export default CollegeCard;
